import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';

const StarRating = ({
    productDetails,
    prev_rating,
    prev_review,
    itemId,
    primary_color,
    secondary_color,
    primary_highlight_color,
    secondary_highlight_color,
    google_review_link,
    branch_name,
}) => {
    const [rating, setRating] = useState(0);
    const [reviewText, setReviewText] = useState('');
    const [selectedStar, setSelectedStar] = useState(0);
    const [productRatings, setProductRatings] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isRatingSubmitted, setIsRatingSubmitted] = useState(false);
    const [isReviewSubmitted, setIsReviewSubmitted] = useState(false);

    
    // Check if we have a Google review link
    const hasGoogleReviewLink = google_review_link && google_review_link.trim() !== '';
    
    // Check if we have any product ratings
    const hasProductRatings = productDetails && productDetails.some(product => product.service_rating > 0);
    
    // Check if we have a previous review
    const hasReview = prev_review && prev_review.trim() !== '';

    useEffect(() => {
        // Initialize product ratings
        if (productDetails) {
            const initialRatings = {};
            productDetails.forEach((product) => {
                initialRatings[product.id] = product.service_rating || 0;
            });
            setProductRatings(initialRatings);
        }

        // Set initial rating if available
        if (prev_rating) {
            setRating(prev_rating);
            setSelectedStar(prev_rating);
            setIsRatingSubmitted(true);
            
        }

        // Set initial review text if available
        if (prev_review) {
            setReviewText(prev_review);
            setIsReviewSubmitted(true);
        }

        // If we have both product ratings and review, consider it submitted
        if (hasProductRatings && hasReview) {
            setIsSubmitted(true);
            setIsRatingSubmitted(true);
        }
        
    }, [productDetails, prev_rating, prev_review, hasProductRatings, hasReview]);

    const handleStarClick = (value) => {
        if (isSubmitted || isRatingSubmitted) return;
        setSelectedStar(value);
        setRating(value);
        
        // Update all product ratings when clicking the main stars
        const updatedRatings = {};
        productDetails.forEach((product) => {
            updatedRatings[product.id] = value;
        });
        setProductRatings(updatedRatings);
    };

    // const handleProductRatingChange = (productId, value) => {
    //     if (isSubmitted) return;
    //     setProductRatings(prev => ({
    //         ...prev,
    //         [productId]: value
    //     }));
    // };

    const handleProductRatingChange = (productId, value) => {
        if (isSubmitted || isRatingSubmitted) return;

    
        // Update productRatings state
        setProductRatings(prev => {
            const updatedRatings = { ...prev, [productId]: value };
    
            // Calculate average rating
            const totalRating = Object.values(updatedRatings).reduce((acc, curr) => acc + curr, 0);
            const avgRating = Math.round(totalRating / Object.keys(updatedRatings).length);
    
            // Update overall rating
            setRating(avgRating);
            setSelectedStar(avgRating);
    
            return updatedRatings;
        });
    };
    

    const handleReviewChange = (e) => {
        if (isSubmitted) return;
        setReviewText(e.target.value);
    };

    const handleSubmitReview = () => {
        let overallRating = rating;
    
        if (hasGoogleReviewLink) {
            const validRatings = Object.values(productRatings).filter(r => r > 0);
            if (validRatings.length > 0) {
                const totalRating = validRatings.reduce((acc, val) => acc + val, 0);
                overallRating = Math.round(totalRating / validRatings.length);
            }
        }
    
        const payload = {
            itemId,
            overallRating: overallRating > 0 ? overallRating : null,
            productRatings,
            google_review_link
        };
    
        if (
            reviewText.trim() &&
            !isReviewSubmitted &&
            (!prev_review || prev_review.trim() === '')
        ) {
            payload.review = reviewText.trim();
        }
    
        fetch(`${process.env.REACT_APP_BASE_URL}/review`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: { 'Content-Type': 'application/json' },
        })
        .then(response => response.json())
        .then(() => {
            if (reviewText.trim() && overallRating > 0) {
                Swal.fire('Success', 'Thanks for helping us improve! Your review is now submitted!', 'success');
                setIsReviewSubmitted(true);
                setIsRatingSubmitted(true);
                setIsSubmitted(true);
            } else if (reviewText.trim()) {
                Swal.fire('Review Submitted', 'Thanks for submitting the review!', 'success');
                setIsReviewSubmitted(true);
            } else if (overallRating > 0) {
                Swal.fire('Rating Submitted', 'Ratings submitted successfully!', 'success');
                setIsRatingSubmitted(true);
            }
        })
        .catch(error => {
            console.error('Error submitting review:', error);
            Swal.fire('Error', 'There was a problem submitting. Please try again.', 'error');
        });
    };
    

    const starRatingStyle = {
        width: '70%',
        marginLeft: '20%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'row-reverse',
        borderTopRightRadius: '75px',
        borderBottomRightRadius: '75px',
        marginBottom: '20px',
        border: 'none',
        position: 'relative',
        background: primary_color,
        boxShadow: '0 1px 1px hsla(0deg 0% 0%/0.075), 0 2px 2px hsla(0deg 0% 0%/0.075), 0 4px 4px hsla(0deg 0% 0%/0.075), 0 8px 8px hsla(0deg 0% 0%/0.075), 0 16px 16px hsla(0deg 0% 0%/0.075)',
    };

    const ratingValueStyle = {
        color: secondary_highlight_color,
        position: 'absolute',
        top: '-7px',
        left: '-61px',
        borderRadius: '50%',
        height: '79px',
        width: '79px',
        background: secondary_color,
        textAlign: 'center',
    };

    return (
        <div className="container">
            {/* Google Review Link (shown when available) */}
            {hasGoogleReviewLink && (
                <div className="row mb-3">
                    <div className="col-12 d-flex justify-content-center align-items-center">
                        <a href={google_review_link} target="_blank" rel="noopener noreferrer">
                            <img
                                src="/assets/images/google_review.png"
                                alt="Google Review"
                                style={{ maxWidth: '200px', cursor: 'pointer' }}
                            />
                        </a>
                    </div>
                </div>
            )}

            {/* Main Star Rating (shown when no Google review link exists) */}
            {!hasGoogleReviewLink && (
                
                <div className="row star-rating" style={starRatingStyle}>
                    <div className="col-12 d-flex justify-content-around" style={{ marginTop: '-5px' }}>
                        {[1, 2, 3, 4, 5].map((starValue) => (
                            <span
                                key={starValue}
                                className={starValue <= selectedStar ? 'star filled' : 'star'}
                                onClick={() => handleStarClick(starValue)}
                                style={(isSubmitted || isRatingSubmitted) ? { pointerEvents: 'none' } : {}}

                                
                            >
                                &#9733;
                            </span>
                        ))}
                    </div>
                    <fieldset className="rating-container">
                        <div className="rating-value" style={ratingValueStyle}>
                            {selectedStar}
                        </div>
                    </fieldset>
                </div>
            )}

            {/* Product-specific ratings */}
            {productDetails && productDetails.length > 0 && (
                <div className="row mt-3">
                    {productDetails.map((product) => (
                        <div key={product.id} className="col-12 mt-2">
                            <div className="d-flex align-items-center justify-content-between">
                                <h6>{product.product_name}</h6>
                                <div>
                                    {[1, 2, 3, 4, 5].map((starValue) => (
                                        <span
                                            key={starValue}
                                            className={starValue <= (productRatings[product.id] || 0) ? 'star filled' : 'star'}
                                            onClick={() => handleProductRatingChange(product.id, starValue)}
                                            style={
                                                isSubmitted || hasProductRatings 
                                                    ? { pointerEvents: 'none' } 
                                                    : {}
                                            }
                                        >
                                            &#9733;
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Review textarea (shown when no review exists) */}
            {!isReviewSubmitted && !isSubmitted &&(
                <div className="row mt-3">
                    <div className="col-12">
                        <textarea
                            rows="5"
                             placeholder={`How was your experience with ${branch_name}?`}
                            className="form-control"
                            value={reviewText}
                            onChange={handleReviewChange}
                            style={isReviewSubmitted ? { pointerEvents: 'none', background: '#eee' } : {}}

                        />
                    </div>
                </div>
            )}

            {/* Submit button (shown when not submitted and either no product ratings or no review exists) */}
            {!(isRatingSubmitted && isReviewSubmitted) && (
                <div className="col-12 mt-3">
                    <button
                        onClick={handleSubmitReview}
                        className="btn btn-primary w-100"
                        style={{ backgroundColor: primary_color, color: primary_highlight_color }}
                    >
                        Submit
                    </button>
                </div>
            )}
            {isReviewSubmitted && !isRatingSubmitted && (
                <div className="row p-2">
                    <div className="col-12 text-center">
                        <p style={{
                            paddingTop: '10px',
                            textAlign: 'center',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            fontFamily: 'serif',
                            color: '#666'
                        }}>
                            Thank you for your review! Now please submit your rating.
                        </p>
                    </div>
                </div>
            )}
            {!isReviewSubmitted && isRatingSubmitted && (
                <div className="row p-2">
                    <div className="col-12 text-center">
                        <p style={{
                            paddingTop: '10px',
                            textAlign: 'center',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            fontFamily: 'serif',
                            color: '#666'
                        }}>
                            Thank you for your ratings! Now please submit your review.
                        </p>
                    </div>
                </div>
            )}



            {/* Thank you message (shown when we have both product ratings and review) */}
            {isRatingSubmitted && isReviewSubmitted && (
                <div className='row p-2'>
                    <div className="review-section col-12">
                        <p style={{
                            paddingTop: '10px',
                            textAlign: 'center',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            fontFamily: 'serif',
                            color: '#666'
                        }}>
                            "Thanks for helping us improve! Your review is now submitted!"
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StarRating;